import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { MessageSquarePlus, Upload, X, Loader2, Camera, Lightbulb, HelpCircle, BookOpen, Sparkles } from 'lucide-react';
import { createTicket } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const ReportIssueWidget = ({ userRole }) => {
  const [open, setOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handleAskEmergent = () => {
    const context = [
      `Page: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `Browser: ${navigator.userAgent.split(' ').slice(-2).join(' ')}`,
    ].join('\n');

    navigator.clipboard.writeText(
      `Issue on SlotFleet:\n${context}\n\nDescription: `
    ).then(() => {
      toast.success('Context copied to clipboard — paste it in Emergent chat');
    }).catch(() => {});

    window.open('https://app.emergent.sh', '_blank');
  };

  const handleScreenshot = (e) => {
    const file = e.target.files?.[0];
    if (file) { setScreenshot(file); setPreview(URL.createObjectURL(file)); }
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setCategory('bug'); setPriority('medium');
    setScreenshot(null); setPreview(null);
  };

  const [suggestTitle, setSuggestTitle] = useState('');
  const [suggestDescription, setSuggestDescription] = useState('');
  const [savingSuggestion, setSavingSuggestion] = useState(false);

  const handleSuggestSubmit = async (e) => {
    e.preventDefault();
    if (!suggestTitle.trim() || !suggestDescription.trim()) { toast.error('Title and description are required'); return; }
    setSavingSuggestion(true);
    try {
      const formData = new FormData();
      formData.append('title', suggestTitle); formData.append('description', suggestDescription);
      formData.append('category', 'suggestion'); formData.append('priority', 'low');
      formData.append('page_url', window.location.href); formData.append('user_agent', navigator.userAgent);
      await createTicket(formData);
      toast.success('Thank you! Your suggestion has been submitted.');
      setSuggestOpen(false); setSuggestTitle(''); setSuggestDescription('');
    } catch { toast.error('Failed to submit suggestion. Please try again.'); }
    finally { setSavingSuggestion(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) { toast.error('Title and description are required'); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', title); formData.append('description', description);
      formData.append('category', category); formData.append('priority', priority);
      formData.append('page_url', window.location.href); formData.append('user_agent', navigator.userAgent);
      if (screenshot) formData.append('screenshot', screenshot);
      await createTicket(formData);
      toast.success('Issue reported successfully! Our team will look into it.');
      setOpen(false); resetForm();
    } catch { toast.error('Failed to submit issue. Please try again.'); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* Header dropdown trigger */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-colors text-amber-500"
            data-testid="fab-toggle"
            aria-label="Help & Feedback"
          >
            <HelpCircle className="w-4 h-4" />
            <span className="text-xs font-semibold hidden sm:inline">Help</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {userRole === 'super_admin' && (
          <DropdownMenuItem onClick={handleAskEmergent} className="cursor-pointer" data-testid="ask-emergent-btn">
            <Sparkles className="w-4 h-4 mr-2 text-violet-500" />
            Ask Emergent AI
          </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate('/dashboard/help')} className="cursor-pointer" data-testid="help-center-btn">
            <BookOpen className="w-4 h-4 mr-2 text-green-500" />
            Help Center
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSuggestOpen(true)} className="cursor-pointer" data-testid="suggest-feature-btn">
            <Lightbulb className="w-4 h-4 mr-2 text-blue-500" />
            Suggest Feature
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpen(true)} className="cursor-pointer" data-testid="report-issue-btn">
            <MessageSquarePlus className="w-4 h-4 mr-2 text-amber-500" />
            Report Issue
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Suggest Feature Dialog */}
      <Dialog open={suggestOpen} onOpenChange={(v) => { setSuggestOpen(v); if (!v) { setSuggestTitle(''); setSuggestDescription(''); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-blue-500" /> Suggest a Feature
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSuggestSubmit} className="space-y-4">
            <div>
              <Label>What feature would you like? *</Label>
              <Input value={suggestTitle} onChange={(e) => setSuggestTitle(e.target.value)} placeholder="e.g., Add bulk export for collections" required data-testid="suggest-title" />
            </div>
            <div>
              <Label>Tell us more *</Label>
              <Textarea value={suggestDescription} onChange={(e) => setSuggestDescription(e.target.value)} placeholder="Describe the feature, how it would help you, and any details..." rows={5} required data-testid="suggest-description" />
            </div>
            <div className="bg-secondary rounded-lg p-3 text-xs text-muted-foreground">
              <p>Your suggestion will be reviewed by our team. We appreciate your feedback!</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setSuggestOpen(false); setSuggestTitle(''); setSuggestDescription(''); }}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600 text-white" disabled={savingSuggestion} data-testid="suggest-submit">
                {savingSuggestion ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Suggestion
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Report Issue Dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-amber-500" /> Report an Issue
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Brief description of the issue" required data-testid="report-issue-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="report-issue-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug / Error</SelectItem>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                    <SelectItem value="ui_issue">UI / Display Issue</SelectItem>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="data_issue">Data Issue</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="report-issue-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What happened? What did you expect? Steps to reproduce..." rows={4} required data-testid="report-issue-description" />
            </div>
            <div>
              <Label>Screenshot (optional)</Label>
              <div className="mt-1">
                {preview ? (
                  <div className="relative inline-block">
                    <img src={preview} alt="Screenshot" className="max-h-40 rounded-lg border border-border" />
                    <button type="button" onClick={() => { setScreenshot(null); setPreview(null); }} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg hover:border-amber-500/50 transition-colors w-full justify-center text-muted-foreground hover:text-foreground">
                    <Camera className="w-4 h-4" />
                    <span className="text-sm">Click to attach a screenshot</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleScreenshot} data-testid="report-issue-screenshot" />
              </div>
            </div>
            <div className="bg-secondary rounded-lg p-3 text-xs text-muted-foreground">
              <p>The following info will be automatically included:</p>
              <ul className="mt-1 space-y-0.5">
                <li>Your account details (name, email, role)</li>
                <li>Current page URL</li>
                <li>Browser and device information</li>
              </ul>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" disabled={saving} data-testid="report-issue-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Issue
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
